import { useCallback, useEffect, useMemo, useState } from "react";
import styled, { ThemeProvider, useTheme } from "styled-components";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Nav, NavItem, Profile } from "@viagogo/core/ui";
import { Feature, useSimpleFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation.ts";
import { useIsStubhub, useIsStubhubUS } from "src/shared/utils/appContextHelper";
import { useWriteUrl } from "src/shared/utils/url-builder";
import { useHomePageModelData } from "src/website-apps/responsive/stubhub-home/hooks/useHomePageModelData";
import { SellNavId } from "src/website-apps/shared/components/header/BrowseHeader";
import {
  createMyAccountNavigation,
  createProfileLinks,
  createSellNavigation,
  exploreLink,
  getFavoritesLink,
  giftCardLink,
  transformcities,
  transformtopLevelCategories,
  useFavoritesOptions,
  useMyFavorites,
  useSellEquivalentLink,
} from "src/website-apps/shared/components/header/headerNavUtils";
import { useListingNotification } from "src/website-apps/shared/components/header/listing-notification/ListingNotification";
import { NavWrapper } from "src/website-apps/shared/components/header/NavWrapper";
import {
  NavItemWrapper,
  StyledHeader,
} from "src/website-apps/shared/components/header/SearchEmphasizedBrowseHeader/styled-components";
import { HeaderCategoryNavigationLinks } from "src/website-apps/shared/components/header/t-home-rebuild-nav/HeaderCategoryNavigationLinks";
import { MENU_Z_INDEX } from "src/website-apps/shared/components/header/t-home-rebuild-nav/HeaderNav/constants";
import { useNavBackdrop } from "src/website-apps/shared/components/header/t-home-rebuild-nav/HeaderNav/NavBackdropContext";
import { transformMarqueeCategoryNavItem } from "src/website-apps/shared/components/header/utils";
import { Logo } from "src/website-apps/shared/components/mega-search/BrowseMegaSearch/components/Logo";
import { AnonBroadwayProfileButton } from "src/website-apps/shared/components/notification-center/AnonBroadwayProfileButton";
import { AnonNotificationBellIcon } from "src/website-apps/shared/components/notification-center/AnonNotificationBellIcon";
import { BroadwayProfileButton } from "src/website-apps/shared/components/notification-center/BroadwayProfileButton";
import { NotificationBellIcon } from "src/website-apps/shared/components/notification-center/NotificationBellIcon";
import { NotificationBellWithPaidSearchGuard } from "src/website-apps/shared/components/notification-center/NotificationBellWithPaidSearchGuard";
import { withSearchContext } from "src/website-apps/shared/components/search/context";
import { useIsHighIntentPaidSearchPage } from "src/website-apps/shared/hooks/useIsHighIntentPaidSearchPage";
import { isPerformerPage } from "src/website-apps/shared/utils/isPerformerPage";
import { isSearchPage } from "src/website-apps/shared/utils/isSearchPage";
import { SearchEmphasizedHeader } from "./SearchEmphasizedHeader";

const BackdropAwareNavItem = ({ title, url, subMenu }) => {
  const { registerOpen, registerClose } = useNavBackdrop();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered && subMenu) {
      registerOpen();
    } else if (!isHovered && subMenu) {
      registerClose();
    }
  }, [isHovered, subMenu, registerOpen, registerClose]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <NavItemWrapper
      style={{ position: "relative", zIndex: MENU_Z_INDEX }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NavItem title={title} url={url} subMenu={subMenu} />
    </NavItemWrapper>
  );
};

const useNavItems = (myFavorites, navLinks, profileLinks) =>
  useMemo(() => [...myFavorites, ...navLinks, profileLinks], [myFavorites, navLinks, profileLinks]);

const navLinks = (sellEquivalentLink, favoritesOptions, listingNotification, appContext) => {
  const sellNavigation = createSellNavigation(sellEquivalentLink, false, SellNavId, appContext, listingNotification);

  const navLinkArray = [sellNavigation, createMyAccountNavigation(false, appContext)];
  if (favoritesOptions?.enabled) {
    navLinkArray.splice(1, 0, getFavoritesLink(favoritesOptions?.content, appContext)); // Second Menu Item
  }

  navLinkArray.unshift(exploreLink(appContext));

  return navLinkArray;
};

const getProfileLinks = (isUserLoggedIn, myAccountBaseUrl, currentUrl, appContext) =>
  createProfileLinks(isUserLoggedIn, true, false, myAccountBaseUrl, currentUrl, appContext);

/**
 * returns a functional component that will render navigation links based on supplied `discoveryLinks`
 * but additionally adds a giftcard link.
 * if no links are supplied then no links will be returned.
 */
const useDiscoveryNav = (discoveryLinks, bigPerformerBanner = false, model = null) => {
  const marqueeNavigationItem = transformMarqueeCategoryNavItem(
    model?.header?.navigationBarLinks?.marqueeNavigationItem,
  );
  const { cities: citiesProp, topLevelCategories } = discoveryLinks || {};
  const cities = useMemo(() => transformcities(citiesProp), [citiesProp]);
  const isStubhub = useIsStubhub();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const isAddNavToVggExp = !isStubhub;

  const TransformedNavMenu = useMemo(
    () =>
      ({ overrideItems, citiesReplacmentNavItem }) => {
        const newNavItems = [...transformtopLevelCategories(overrideItems, {}, isAddNavToVggExp)];
        if (citiesReplacmentNavItem) {
          newNavItems.push(citiesReplacmentNavItem);
        } else {
          newNavItems.push(...cities);
        }
        return (
          <HeaderCategoryNavigationLinks
            navModel={newNavItems}
            bigPerformerBanner={bigPerformerBanner}
            useCondensedCityLabel
            showChevron={false}
          />
        );
      },
    [bigPerformerBanner, cities, isAddNavToVggExp],
  );

  const updatedNavItems = isStubhub && !isMobile && !!model?.header?.navigationCategories;

  return useMemo(
    () => () => (
      <NavWrapper>
        <Nav>
          {updatedNavItems && (
            <TransformedNavMenu
              overrideItems={model?.header?.navigationCategories}
              citiesReplacmentNavItem={marqueeNavigationItem}
            />
          )}
          {!isStubhub && (
            <TransformedNavMenu overrideItems={topLevelCategories} citiesReplacmentNavItem={marqueeNavigationItem} />
          )}
        </Nav>
      </NavWrapper>
    ),
    [
      TransformedNavMenu,
      isStubhub,
      marqueeNavigationItem,
      model?.header?.navigationCategories,
      topLevelCategories,
      updatedNavItems,
    ],
  );
};

const ProfileSectionContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  align-items: center;
`;

const SearchEmphasizedBrowseHeaderThemeProvider = ({ theme, children }) => (
  <ThemeProvider theme={theme}>{children}</ThemeProvider>
);

/**
 * TODO: Clean this up. Make shared logic between this and HeaderConfiguration.Browse. Or just make them one component.
 */
const SearchEmphasizedBrowseHeaderComponent = ({ discoveryLinks, isUserLoggedIn, bigPerformerBanner, greeting }) => {
  const appContext = useAppContext();
  const isStubhubUS = useIsStubhubUS();
  const model = useHomePageModelData();

  const isNavCleanup = useSimpleFeatureTernary(FeatureSetEnum.CXWD_TopNavCleanup, false, true) ?? false;
  const isDesktop = useMediaQuery(Breakpoint.Desktop);

  const sellEquivalentLink = useSellEquivalentLink();

  const favoritesOptions = useFavoritesOptions();

  const DiscoveryNav = useDiscoveryNav(discoveryLinks, bigPerformerBanner, model);

  const headerTheme = useTheme();

  const myFavorites = useMyFavorites();
  const listingNotification = useListingNotification();
  const selectedNavLinks = navLinks(sellEquivalentLink, favoritesOptions, listingNotification, appContext);
  const windowLocation = useWindowLocation();

  const profileLinks = getProfileLinks(isUserLoggedIn, appContext.myAccountBaseUrl, windowLocation.href, appContext);
  const allNavItems = useNavItems(myFavorites, selectedNavLinks, profileLinks);
  // HomeHeaderLayout renders this header for tablet and desktop; gate the filter to desktop only
  // (matches BrowseHeader/SimpleHeader scope) and skip anon users (their profileLinks.id is "SignIn"
  // and the anon icon is already hidden).
  const mainNavItemOrder = useMemo(
    () =>
      isNavCleanup && isDesktop && isUserLoggedIn
        ? allNavItems.filter((item) => item.id !== profileLinks.id)
        : allNavItems,
    [allNavItems, isNavCleanup, isDesktop, isUserLoggedIn, profileLinks.id],
  );

  const mainNavItemMaybeWithNewNavigation = [...mainNavItemOrder];
  if (isStubhubUS && !isNavCleanup) {
    mainNavItemMaybeWithNewNavigation.unshift(giftCardLink(appContext));
  }

  const isHighIntentPaidSearch = useIsHighIntentPaidSearchPage(appContext.appName);
  const myAccountUrl = useWriteUrl("/secure/myaccount");

  return (
    <SearchEmphasizedBrowseHeaderThemeProvider theme={headerTheme}>
      <StyledHeader $isLeftAlignNav={isPerformerPage(appContext)}>
        <SearchEmphasizedHeader
          transparentBg={false}
          greeting={greeting}
          logo={() => <Logo />}
          mainNav={() => (
            <Nav>
              {mainNavItemMaybeWithNewNavigation.map(({ id, title, url, subMenu }) => (
                <BackdropAwareNavItem key={id} title={title} url={url} subMenu={subMenu} />
              ))}
            </Nav>
          )}
          discoveryNav={() => (isSearchPage(appContext) ? null : <DiscoveryNav />)}
          profileIcon={() => (
            <ProfileSectionContainer>
              {isUserLoggedIn ? (
                <Feature
                  id={FeatureSetEnum.NotificationCenterBellIcon}
                  canParticipate={() => isUserLoggedIn}
                  control={() => <Profile profileLink={myAccountUrl} />}
                  variant={() => (
                    <NotificationBellWithPaidSearchGuard
                      isHighIntentPaidSearch={isHighIntentPaidSearch}
                      fallback={<Profile profileLink={myAccountUrl} />}
                    >
                      <BroadwayProfileButton />
                      <NotificationBellIcon />
                    </NotificationBellWithPaidSearchGuard>
                  )}
                />
              ) : (
                <Feature
                  id={FeatureSetEnum.AnonNotificationCenterUpsell}
                  canParticipate={() => true}
                  control={() => (isNavCleanup ? null : <Profile profileLink={myAccountUrl} />)}
                  variant={() =>
                    isNavCleanup ? (
                      <AnonNotificationBellIcon />
                    ) : (
                      <>
                        <AnonBroadwayProfileButton />
                        <AnonNotificationBellIcon />
                      </>
                    )
                  }
                />
              )}
            </ProfileSectionContainer>
          )}
        />
      </StyledHeader>
    </SearchEmphasizedBrowseHeaderThemeProvider>
  );
};

export const SearchEmphasizedBrowseHeader = withSearchContext(SearchEmphasizedBrowseHeaderComponent);
